import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect } from 'react';

export interface SwitchboardCall {
  id: string;
  company_id: string;
  direction: string;
  caller_number: string | null;
  callee_number: string | null;
  twilio_call_sid: string | null;
  status: string;
  duration_seconds: number;
  answered_by: string | null;
  transferred_to: string | null;
  customer_id: string | null;
  lead_id: string | null;
  ivr_selection: string | null;
  notes: string | null;
  recording_url: string | null;
  started_at: string;
  answered_at: string | null;
  ended_at: string | null;
  created_at: string;
  // Joined customer name
  customer_name?: string | null;
}

export interface IvrMenu {
  id: string;
  company_id: string;
  greeting_text: string;
  options: { digit: string; label: string; action: string; queue_name: string; ring_group_id?: string }[];
  fallback_action: string;
  fallback_queue: string;
  hold_music_url: string | null;
  is_active: boolean;
  after_hours_message: string | null;
  business_hours_start: string | null;
  business_hours_end: string | null;
  business_days: number[] | null;
  holiday_closed: boolean;
  holiday_message: string | null;
  holiday_start_date: string | null;
  holiday_end_date: string | null;
}

export function useSwitchboardCalls(filter?: 'active' | 'history') {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['switchboard-calls', user?.company_id, filter],
    queryFn: async () => {
      if (!user?.company_id) return [];
      let q = (supabase as any)
        .from('switchboard_calls')
        .select('*, customers:customer_id(name)')
        .eq('company_id', user.company_id)
        .order('created_at', { ascending: false });

      if (filter === 'active') {
        q = q.in('status', ['ringing', 'queued', 'in-progress']);
      } else if (filter === 'history') {
        q = q.in('status', ['completed', 'no-answer', 'busy', 'failed', 'canceled']);
        q = q.limit(500);
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data || []).map((row: any) => ({
        ...row,
        customer_name: row.customers?.name || null,
        customers: undefined,
      })) as SwitchboardCall[];
    },
    enabled: !!user,
    refetchInterval: filter === 'active' ? 5000 : undefined,
  });

  // Realtime subscription for active calls
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`switchboard-live-${filter ?? 'all'}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'switchboard_calls',
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['switchboard-calls'] });
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, queryClient]);

  return query;
}

export function useUpdateSwitchboardCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & Partial<SwitchboardCall>) => {
      const { error } = await (supabase as any)
        .from('switchboard_calls')
        .update(updates)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['switchboard-calls'] }),
  });
}

export function useIvrMenu() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['ivr-menu', user?.company_id],
    queryFn: async () => {
      if (!user?.company_id) return null;
      const { data, error } = await (supabase as any)
        .from('ivr_menus')
        .select('*')
        .eq('company_id', user.company_id)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as IvrMenu | null;
    },
    enabled: !!user?.company_id,
  });
}

export function useUpsertIvrMenu() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (menu: Partial<IvrMenu>) => {
      if (!user?.company_id) throw new Error('No company');
      const { error } = await (supabase as any)
        .from('ivr_menus')
        .upsert({ ...menu, company_id: user.company_id }, { onConflict: 'company_id' });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ivr-menu'] }),
  });
}

export const CALL_STATUS_LABELS: Record<string, string> = {
  'ringing': 'Ringer',
  'queued': 'I kø',
  'in-progress': 'I gang',
  'completed': 'Afsluttet',
  'no-answer': 'Ikke svaret',
  'busy': 'Optaget',
  'failed': 'Fejlet',
  'canceled': 'Annulleret',
};
